import {Offer as SapiOffer} from '@findhotel/sapi'

import {ChargeType} from '../../analytics/types/Events'
import {Source} from '../../api-types/bovio/response/booking'
import {OfferTagLabel} from '../types/offer'
import {Offer} from '../types/offer'
import {SearchOffer} from '../types/SearchOffer'
import {
  hasChargeLaterPayment,
  hasPayAtPropertyPayment
} from './offerPaymentMode'
export const PAY_AT_PROPERTY_METHOD = 'pay_at_property'
const PROVIDER_REDIRECT_ANONYMOUS_ID_PARAM_NAME = 'aid'

/**
 * Receives bookUri and anonymousId and merges them in valid URL string
 * @param anonymousId - string
 * @param bookUri - string
 */
export const augmentBookUriWithAnonymousId = (
  anonymousId: string,
  bookUri: string
) => `${bookUri}&${PROVIDER_REDIRECT_ANONYMOUS_ID_PARAM_NAME}=${anonymousId}`

/**
 * Get a lowercase roomName string.
 * Used together with a `text-transform: capitalize` style to show roomNames capitalized, instead of all caps.
 * @param offer - SearchOffer
 */
export const getOfferRoomName = (offer: SearchOffer): string => {
  const roomName = (offer?.roomName || '').toLowerCase()
  return roomName.charAt(0).toUpperCase() + roomName.slice(1)
}

/**
 * A factory function that determines the different statuses of an offer based on a given set of labels
 * @param offer - SapiOffer
 * @param label - OfferTagLabel
 * @returns boolean
 */
export const hasOfferTag = (
  offer: Pick<SapiOffer, 'tags'> | Pick<Offer, 'tags'>,
  label: OfferTagLabel
): boolean => {
  if (offer?.tags?.includes(label)) return true
  return false
}

/**
 * Factory function that returns charge type for offer based on charge later eligibility or pay at property
 * @param offer - SapiOffer
 * @returns string
 */
export const getChargeType = (
  offer: Pick<
    SapiOffer,
    | 'cancellationPenalties'
    | 'providerCode'
    | 'package'
    | 'paymentMode'
    | 'tags'
  >
): Source[] | [typeof PAY_AT_PROPERTY_METHOD] => {
  if (hasChargeLaterPayment(offer)) return [Source.ChargeLater]
  if (hasPayAtPropertyPayment(offer)) return [PAY_AT_PROPERTY_METHOD]
  return [Source.Default]
}

/**
 * A type that represents the options for getting charge types.
 * @param isExistingBooking - boolean, whether this is an existing booking
 * @param isChargeLaterEligible - boolean, wether its charge later eligible
 * @param isPayAtProperty - boolean, wether its pay at property
 */
interface GetChargeTypesOptions {
  isExistingBooking?: boolean
  isChargeLaterEligible: boolean
  isPayAtProperty: boolean
}

/**
 * Get charge types for offer based on booking info, charge later eligibility and pay at property
 * @param options - GetChargeTypesOptions
 * @returns ChargeType[]
 */
export const getChargeTypes = (
  options: GetChargeTypesOptions
): ChargeType[] => {
  const {
    isExistingBooking = false,
    isChargeLaterEligible,
    isPayAtProperty = false
  } = options
  return isPayAtProperty
    ? [ChargeType.PayLater]
    : [
        ChargeType.Default,
        ...(!isExistingBooking && isChargeLaterEligible
          ? [ChargeType.ChargeLater]
          : [])
      ]
}
